import React from 'react';
import PropTypes from 'prop-types';
import {fromJS} from 'immutable';
import moment from 'moment';
import {withRouter} from 'react-router';
import {stats} from 'lib/StatsUtil';
import {pluralize} from 'lib/stringUtils';
import {renderDuration} from 'lib/timeUtils';
import {setUpStore} from 'client/framework';
import {makeNavigateToQuestion} from 'client/Classrooms/ClassroomUtil';
import {resource} from '@albert-io/json-api-framework/request/builder';
import Button from 'sg/Button/Button.react';
import SettingsTableStore from 'sg/Tables/SettingsTable/SettingsTable.store';
import SettingsTable from 'sg/Tables/SettingsTable/SettingsTable.react';
import {Column} from 'sg/Tables/MandarkTable/MandarkTable.react';
import FlatBarChart from 'generic/FlatBarChart/FlatBarChart.react';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import QuestionDifficultyLabel from 'generic/QuestionDifficultyLabel/QuestionDifficultyLabel.react';

import EmptyInsightsReport from '../../EmptyInsightsReport/EmptyInsightsReport.react';
import classroomInsightsStore from '../../../ClassroomInsights.store';
import './questions-table.scss';

const settingsTableStore = setUpStore(SettingsTableStore, 'StandardInsightsQuestionsTableStore');

class QuestionsTable extends React.Component {
  static propTypes = {
    location: PropTypes.shape({
      query: PropTypes.shape({
        attempt: PropTypes.string,
        startDate: PropTypes.string,
        endDate: PropTypes.string,
        guideLevelId: PropTypes.string,
        subjectId: PropTypes.string
      })
    }),
    params: PropTypes.shape({
      classId: PropTypes.string,
      standardId: PropTypes.string
    })
  };

  getQuestionsQuery() {
    const {attempt, startDate, endDate, guideLevelId, subjectId} = this.props.location.query;

    const guessContext = {};
    if (attempt === 'mostRecent') {
      guessContext.most_recent = true;
    } else {
      guessContext.attempt_number = attempt;
    }

    return resource('questions_v2')
      .mandarkEndpoint(['labels_v1', this.props.params.standardId, 'questions_v2'])
      .include('question_set_v1')
      .filter({
        question_set_v1: {
          category: {
            not: 'exam'
          },
          subject_v2: subjectId
        }
      })
      .filter(
        {
          question_set_v1: {
            guide_levels_v2: guideLevelId
          }
        },
        !!guideLevelId
      )
      .filter(
        {
          question_set_v1: {
            guide_levels_v2: {
              nlevel: 1
            }
          }
        },
        !guideLevelId
      )
      .withMeta('question_v2')
      .meta({
        context: {
          classroom: {
            id: this.props.params.classId,
            insights: true
          },
          end_date: endDate,
          guess: guessContext,
          start_date: startDate
        }
      });
  }

  renderAccuracy(question) {
    const studentsWithCorrectGuess = question.getClassroomInsightsCountCorrectStudents();
    const totalStudentsWithGuesses =
      studentsWithCorrectGuess + question.getClassroomInsightsCountIncorrectStudents();
    return `${stats.percentage(studentsWithCorrectGuess, totalStudentsWithGuesses)}%`;
  }

  renderAnswerBreakdown(question) {
    const studentsWithCorrectGuess = question.getClassroomInsightsCountCorrectStudents();
    const studentsWithIncorrectGuess = question.getClassroomInsightsCountIncorrectStudents();
    const studentsWithGuess = studentsWithCorrectGuess + studentsWithIncorrectGuess;
    const studentsWithUnansweredGuess = question.getClassroomInsightsCountUnansweredStudents();
    const totalStudents =
      studentsWithCorrectGuess + studentsWithIncorrectGuess + studentsWithUnansweredGuess;

    const performanceData = fromJS([
      {
        fillColorClass: 'classroom-insights__performance--correct',
        fillPercentage: stats.percentage(studentsWithCorrectGuess, totalStudents),
        keyLabel: 'Correct'
      },
      {
        fillColorClass: 'classroom-insights__performance--incorrect',
        fillPercentage: stats.percentage(studentsWithIncorrectGuess, totalStudents),
        keyLabel: 'Incorrect'
      },
      {
        fillColorClass: 'classroom-insights__performance--unanswered',
        fillPercentage: stats.percentage(studentsWithUnansweredGuess, totalStudents),
        keyLabel: 'Not Answered'
      }
    ]);

    return (
      <div className='usage-report__performance'>
        <small>
          {studentsWithGuess} {pluralize('total attempt', studentsWithGuess)}
        </small>
        <FlatBarChart inline data={performanceData} />
      </div>
    );
  }

  renderCorrectAttempts(question) {
    return question.getClassroomInsightsCountCorrectStudents();
  }

  renderDifficulty(question) {
    return (
      <QuestionDifficultyLabel
        className='standard-insights-questions-table__difficulty'
        difficulty={question.getDifficulty()}
        short
      />
    );
  }

  renderIncorrectAttempts(question) {
    return question.getClassroomInsightsCountIncorrectStudents();
  }

  renderTimeSpent(question) {
    const timeSpent = moment.duration(question.getClassroomInsightsAverageTimeSpent());
    const formattedTimeSpent = renderDuration(timeSpent, {style: 'hh:mm:ss'});
    return formattedTimeSpent;
  }

  renderTitle(question) {
    return <MarkdownRendererV2 text={question.getTitle()} />;
  }

  renderTotalAttempts(question) {
    return (
      question.getClassroomInsightsCountCorrectStudents() +
      question.getClassroomInsightsCountIncorrectStudents()
    );
  }

  renderViewQuestionButton = (question) => {
    const questionSetSlug = question.getQuestionSet().getSlugId();
    const questionSlug = question.getSlugId();
    return (
      <Button
        text='View question'
        whiteBg
        small
        onClick={(e) => {
          e.stopPropagation();
          makeNavigateToQuestion(questionSetSlug, questionSlug)();
        }}
      />
    );
  };

  render() {
    const emptyResultsHeader = settingsTableStore.getSearchTerm()
      ? `No questions matching your search term for this standard.`
      : 'No questions for this standard yet.';

    const emptyResultsContent = settingsTableStore.getSearchTerm()
      ? 'Once there is at least 1 question matching your search term, for this standard, those questions will appear here.'
      : 'Once there is at least 1 question for this standard, those questions will appear here.';

    return (
      <SettingsTable
        emptyTableElement={
          <EmptyInsightsReport header={emptyResultsHeader} content={emptyResultsContent} />
        }
        query={this.getQuestionsQuery()}
        searchFilters={classroomInsightsStore.getQuestionsSearchFilters}
        storeName={settingsTableStore.getName()}
      >
        <Column
          align='center'
          defaultVisible
          content={this.renderDifficulty}
          heading='Difficulty'
          sortBy='difficulty'
        />
        <Column
          defaultSortParam
          defaultVisible
          content={this.renderTitle}
          heading='Title'
          sortBy='title'
        />
        <Column
          align='right'
          content={this.renderTimeSpent}
          defaultVisible
          heading='Average Time Spent'
          sortBy='meta.classroom_insights_average_time_spent'
        />
        <Column
          align='center'
          defaultVisible
          heading='Answer Breakdown'
          content={this.renderAnswerBreakdown}
        />
        <Column align='right' defaultVisible heading='Accuracy' content={this.renderAccuracy} />
        <Column
          align='right'
          heading='Correct Attempts'
          content={this.renderCorrectAttempts}
          sortBy='meta.classroom_insights_count_correct_students'
        />
        <Column
          align='right'
          heading='Incorrect Attempts'
          content={this.renderIncorrectAttempts}
          sortBy='meta.classroom_insights_count_incorrect_students'
        />
        <Column align='right' heading='Total Attempts' content={this.renderTotalAttempts} />
        <Column alwaysVisible align='center' content={this.renderViewQuestionButton} />
      </SettingsTable>
    );
  }
}

export default withRouter(QuestionsTable);
